import React from "react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { ShieldCheck, Award, Building2 } from "lucide-react";

const stats = [
  {
    id: 1,
    icon: <ShieldCheck className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 text-blue-200" />,
    number: 130,
    suffix: "+",
    title: "Tech ECoreXperts & Security Specialists",
    delay: 0,
  },
  {
    id: 2,
    icon: <Award className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 text-blue-200" />,
    number: 45,
    suffix: "+",
    title: "Industry Certifications & Recognitions",
    delay: 0.2,
  },
  {
    id: 3,
    icon: <Building2 className="w-9 h-9 sm:w-11 sm:h-11 lg:w-12 lg:h-12 text-blue-200" />,
    number: 2,
    suffix: "",
    title: "Our Offices",
    delay: 0.4,
  },
];

const Facts = () => {
  return (
    <section className="relative py-12 sm:py-14 lg:py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white overflow-hidden">
      {/* Background Lights */}
      <div className="absolute -top-28 -left-28 w-56 sm:w-72 h-56 sm:h-72 bg-blue-500/25 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-0 right-0 w-72 sm:w-96 h-72 sm:h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse-slow"></div>

      {/* Decorative Wave Glow */}
      <div className="absolute inset-0 opacity-10 pointer-events-none hidden sm:block">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <pattern id="wave-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M0 50 Q25 0 50 50 T100 50" stroke="none" fill="currentColor" opacity="0.2" />
          </pattern>
          <rect width="100" height="100" fill="url(#wave-pattern)" />
        </svg>
      </div>

      {/* Main Wrapper */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Heading */}
        <div className="text-center mb-10 sm:mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.5 }}
            className="font-extrabold text-blue-200 drop-shadow-lg 
              text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight"
          >
            Impactful Numbers Reflecting Our Commitment
          </motion.h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 mt-4 max-w-2xl mx-auto leading-relaxed">
            Our numbers speak volumes about our dedication to excellence:
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10 lg:gap-12">
          {stats.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: item.delay }}
              viewport={{ once: true, amount: 0.5 }}
              className="relative group bg-white/10 backdrop-blur-md rounded-3xl p-6 sm:p-8 text-center shadow-xl border border-white/20 
              hover:shadow-blue-500/30 transition-all duration-300"
            >
              {/* Icon Animation */}
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: item.delay }}
                className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center 
                bg-blue-400/10 rounded-full border border-blue-300/20"
              >
                {item.icon}
              </motion.div>

              {/* Counter Number */}
              <h3 className="font-extrabold text-blue-100 mb-1 text-4xl sm:text-5xl md:text-6xl">
                <CountUp end={item.number} duration={2.5} separator="," start={0} />
                {item.suffix}
              </h3>

              <p className="text-xs sm:text-sm md:text-base font-medium text-blue-200 mt-2 px-1">
                {item.title}
              </p>

              {/* Hover Glow Border */}
              <div className="absolute inset-0 rounded-3xl border border-blue-300/10 group-hover:border-blue-400/40 transition-all duration-500 pointer-events-none"></div>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          viewport={{ once: true, amount: 0.5 }}
          className="text-center mt-12 sm:mt-14 text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed"
        >
          These figures underscore our global reach, industry expertise, and a track record of
          excellence in delivering IT solutions in Hyderabad and beyond.
        </motion.p>
      </div>

      {/* Keyframes */}
      <style>
        {`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        .animate-pulse-slow {
          animation: pulse-slow 10s ease-in-out infinite;
        }
        `}
      </style>
    </section>
  );
};

export default Facts;
